import React, { useState } from 'react';
import { getIntl } from '../../../../utils/intl';
import useOrganizationGroups from '../../../../hooks/api/useOrganizationGroups';
import useOrganization from '../../../../hooks/api/useOrganization';
import Select from '../../../v2BaseComponents/Inputs/Select';
import { SplitFormRow } from '../../../v2BaseComponents/Form';

const intl = getIntl('LocationOrganizationGroupField', {
    location: 'Location',
    other: 'Other',
});

/** Groups are also used as plain tags, so only those with an address belong in a Location picker. */
const groupHasAddress = (group: {
    contactDetails: { address1?: string | null; city?: string | null; state?: string | null; zipCode?: string | null };
}) =>
    !!(
        group.contactDetails.address1 ||
        group.contactDetails.city ||
        group.contactDetails.state ||
        group.contactDetails.zipCode
    );

/** Sentinel value for the manual-entry "Other" option (no group id collides with it). */
const OTHER_LOCATION_VALUE = '__other__';

/**
 * "Location" select for org visit flows — lists Organization Groups that have an address
 * (tag-only groups are excluded) plus an "Other" option for a one-off manual address. On
 * select, populates the visit address fields from the group's stored address; "Other"
 * clears them for manual entry. Fields stay editable afterwards.
 */
const LocationOrganizationGroupFieldInner: React.FC<LocationFieldProps> = ({
    isDisabled,
    setFieldValue,
    onLocationSelected,
}) => {
    const { groups, isLoadingGroups } = useOrganizationGroups({});
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

    const locationGroups = groups.filter(groupHasAddress);

    return (
        <SplitFormRow>
            <Select
                title={intl('location')}
                value={selectedGroupId}
                isDisabled={isDisabled || isLoadingGroups}
                testID="visit-location-group"
                onChange={(value) => {
                    const selection = value as string | null;
                    setSelectedGroupId(selection);
                    onLocationSelected?.();
                    if (selection === OTHER_LOCATION_VALUE) {
                        // Clear so the user types a one-off address into the (now revealed) fields.
                        setFieldValue('resource.address1', null);
                        setFieldValue('resource.address2', null);
                        setFieldValue('resource.city', null);
                        setFieldValue('resource.state', null);
                        setFieldValue('resource.zipCode', null);
                        return;
                    }
                    const group = locationGroups.find((_) => _.organizationGroupId === selection);
                    if (!group) return;
                    setFieldValue('resource.address1', group.contactDetails.address1 || null);
                    setFieldValue('resource.address2', group.contactDetails.address2 || null);
                    setFieldValue('resource.city', group.contactDetails.city || null);
                    setFieldValue('resource.state', group.contactDetails.state || null);
                    setFieldValue('resource.zipCode', group.contactDetails.zipCode || null);
                }}
                selectOptions={[
                    ...locationGroups.map((group) => ({
                        label: group.metadata.name,
                        value: group.organizationGroupId,
                        testID: group.metadata.name,
                    })),
                    { label: intl('other'), value: OTHER_LOCATION_VALUE, testID: 'visit-location-other' },
                ]}
            />
        </SplitFormRow>
    );
};

type LocationFieldProps = {
    isDisabled?: boolean;
    setFieldValue: (field: string, value: any) => void;
    /** Fires when the user picks a location (org flows reveal the address inputs then). */
    onLocationSelected?: () => void;
};

/** Renders nothing when the signed-in user has no organization (family app safety). */
const LocationOrganizationGroupField: React.FC<LocationFieldProps> = (props) => {
    const { myOrganization } = useOrganization({});
    if (!myOrganization?.organization?.organizationId) return null;
    return <LocationOrganizationGroupFieldInner {...props} />;
};

export default React.memo(LocationOrganizationGroupField);
